import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { setSelectedSection, setUser, setTopics, setPermissions } from '../store/dashboardSlice'
import Cookies from 'js-cookie'
import GlobalThrobberComponent from '../components/global_throbber_component'
import { useNotification } from '../components/global_noification_component'
import ItemCatalogComponent from '../components/item_catalog_component'
import ProfileComponent from '../components/profile_component'
import KnowledgeBaseComponent from '../components/knowledge_base_component'
import '../css/dashboard_page.css'
import { motion, AnimatePresence } from 'framer-motion'

const COLLAPSE_ICON = 'https://cdn.ballhead.app/web_assets/double-left-50.png'
const EXPAND_ICON = 'https://cdn.ballhead.app/web_assets/double-right-50.png'
const API_BASE = process.env.REACT_APP_API_BASE
const API_VERSION = process.env.REACT_APP_API_ENVIRONMENT
const OAUTH_PAGE = process.env.REACT_APP_API_DISCORD_OAUTH_PAGE

export default function DashboardPage({ setNavbarVisible }) {
  const selectedSection = useSelector(state => state.dashboard.selectedSection)
  const user = useSelector(state => state.dashboard.user)
  const permissions = useSelector(state => state.dashboard.permissions)
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [eligible, setEligible] = useState(false)
  const notify = useNotification()
  const navigate = useNavigate()

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)
  const [isKbNavbarMode, setIsKbNavbarMode] = useState(false)
  const [kbPanelWidth, setKbPanelWidth] = useState(() => {
    const w = parseInt(localStorage.getItem('kb_panel_width'), 10)
    return Number.isFinite(w) ? w : 280
  })
  const isKbDragging = useRef(false)
  const kbDragStartX = useRef(0)
  const kbDragStartWidth = useRef(0)

  useEffect(() => {
    if (setNavbarVisible) {
      setNavbarVisible(!isKbNavbarMode)
    }
    return () => {
      if (setNavbarVisible) {
        setNavbarVisible(true)
      }
    }
  }, [isKbNavbarMode, setNavbarVisible])

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  useEffect(() => {
    if (isMobile) return
    const timeoutId = setTimeout(() => {
      localStorage.setItem('kb_panel_width', String(kbPanelWidth))
    }, 150)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [kbPanelWidth, isMobile])

  const startKbResize = e => {
    if (isMobile) return
    e.preventDefault()
    isKbDragging.current = true
    kbDragStartX.current = e.clientX
    kbDragStartWidth.current = kbPanelWidth
  }
  useEffect(() => {
    if (isMobile) return
    let animationFrame
    const move = e => {
      if (isKbDragging.current) {
        if (animationFrame) return
        animationFrame = requestAnimationFrame(() => {
          const minW = 280
          const maxW = window.innerWidth * 0.5
          const next = Math.min(maxW, Math.max(minW, kbDragStartWidth.current + e.clientX - kbDragStartX.current))
          setKbPanelWidth(next)
          animationFrame = null
        })
      }
    }
    const up = () => {
      isKbDragging.current = false
      if (animationFrame) {
        cancelAnimationFrame(animationFrame)
        animationFrame = null
      }
    }
    document.addEventListener('mousemove', move, { passive: true })
    document.addEventListener('mouseup', up)
    return () => {
      document.removeEventListener('mousemove', move)
      document.removeEventListener('mouseup', up)
      if (animationFrame) {
        cancelAnimationFrame(animationFrame)
      }
    }
  }, [isMobile])

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
      const isCtrl = e.ctrlKey || e.metaKey
      if (isCtrl && e.key === 'k') {
        e.preventDefault()
        if (permissions?.knowledge_base) {
          dispatch(setSelectedSection('Knowledge Base'))
          setIsKbNavbarMode(true)
        }
        return
      }
      if (isCtrl && e.key === 'i') {
        e.preventDefault()
        if (permissions?.gc_items) {
          dispatch(setSelectedSection('Item Category'))
          setIsKbNavbarMode(false)
        }
        return
      }
      if (isCtrl && e.key === 'p') {
        e.preventDefault()
        dispatch(setSelectedSection('Profile'))
        setIsKbNavbarMode(false)
        return
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [selectedSection, permissions])

  const fetchTopics = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/${API_VERSION}/private/knowledge-base/topics`, {
        method: 'GET',
        credentials: 'include'
      })
      if (!res.ok) throw new Error('Failed to load topics')
      const data = await res.json()
      const parseOptions = val => {
        if (Array.isArray(val)) return val
        if (typeof val === 'string') {
          try {
            const arr = JSON.parse(val)
            return Array.isArray(arr) ? arr : []
          } catch {
            return []
          }
        }
        return []
      }
      const getTopicId = t => String(t.topic_id ?? t.id ?? t.PK)
      const map = {}
      data.topics.forEach(t => {
        const id = String(getTopicId(t))
        const node = { ...t, dropdownOptions: parseOptions(t.dropdownOptions ?? t.dropdown_options), children: [] }
        map[id] = node
        if (t.PK && String(t.PK) !== id) map[String(t.PK)] = node
        if (t.topic_id && String(t.topic_id) !== id) map[String(t.topic_id)] = node
        if (t.id && String(t.id) !== id) map[String(t.id)] = node
      })
      const roots = []
      data.topics.forEach(t => {
        const id = String(getTopicId(t))
        const parentKey = t.parentId ?? t.parent_id ?? t.parentTopicId ?? t.parent_topic_id ?? t.parentid
        if (parentKey != null && map[String(parentKey)]) {
          map[String(parentKey)].children.push(map[id])
        } else {
          roots.push(map[id])
        }
      })
      dispatch(setTopics(roots))
    } catch {
      notify('error', 'Unable to fetch topics')
    }
  }, [API_BASE, API_VERSION, notify, dispatch])

  useEffect(() => {
    const verifyUser = async () => {
      const token = Cookies.get('refresh_token')
      if (!token) {
        window.location.href = OAUTH_PAGE
        return
      }
      try {
        const res = await fetch(`${API_BASE}/${API_VERSION}/public/oAuth/discord/fetch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: token, application: 'dashboard' })
        })
        const data = await res.json()
        if (!res.ok) {
          notify('error', data.message || 'Authentication failed')
          navigate('/')
          return
        }
        dispatch(setUser(data.user))
        dispatch(setPermissions(data.permissions))
        Cookies.set('discord_id', data.user.id, { sameSite: 'strict' })
        Cookies.set('username', data.user.username, { sameSite: 'strict' })
        Cookies.set('global_name', data.user.global_name, { sameSite: 'strict' })
        Cookies.set('avatar', data.user.avatar, { sameSite: 'strict' })
        Cookies.set('banner', data.user.banner, { sameSite: 'strict' })
        Cookies.set('email', data.user.email, { sameSite: 'strict' })
        Cookies.set('code', data.code, { sameSite: 'strict' })
        Cookies.set('refresh_token', data.refresh_token, { sameSite: 'strict' })
        setEligible(data.is_in_gym_guild)
        const cached = localStorage.getItem('kb_topic_cache')
        if (cached) {
          try {
            const flat = JSON.parse(cached)
            if (Array.isArray(flat)) {
              const map = new Map()
              flat.forEach(i => {
                const id = String(i.node.topic_id ?? i.node.id ?? i.node.PK)
                map.set(id, { ...i.node, children: [] })
              })
              flat.forEach(i => {
                const id = String(i.node.topic_id ?? i.node.id ?? i.node.PK)
                if (i.node.parentId && map.has(i.node.parentId)) {
                  map.get(i.node.parentId).children.push(map.get(id))
                }
              })
              const roots = []
              map.forEach(n => {
                if (!n.parentId || !map.has(n.parentId)) roots.push(n)
              })
              dispatch(setTopics(roots))
            } else {
              fetchTopics()
            }
          } catch {
            fetchTopics()
          }
        } else {
          fetchTopics()
        }
      } catch {
        notify('error', 'Failed verifying user')
        navigate('/')
        return
      } finally {
        setLoading(false)
      }
    }
    verifyUser()
  }, [fetchTopics])

  useEffect(() => {
    if (permissions) {
      if (selectedSection === 'Knowledge Base' && !permissions.knowledge_base) {
        dispatch(setSelectedSection('Profile'))
        setIsKbNavbarMode(false)
      }
      if (selectedSection === 'Item Category' && !permissions.gc_items) {
        dispatch(setSelectedSection('Profile'))
        setIsKbNavbarMode(false)
      }
    }
  }, [permissions, selectedSection])

  const renderContent = () => {
    switch (selectedSection) {
      case 'Profile':
        return <ProfileComponent setIsKbNavbarMode={setIsKbNavbarMode} />
      case 'Knowledge Base':
        return permissions?.knowledge_base ? (
          <KnowledgeBaseComponent
            isKbNavbarMode={isKbNavbarMode}
            kbPanelWidth={kbPanelWidth}
            startKbResize={startKbResize}
            isMobile={isMobile}
          />
        ) : null
      case 'Item Category':
        return permissions?.gc_items ? <ItemCatalogComponent /> : null
      default:
        return null
    }
  }

  if (loading) {
    return <GlobalThrobberComponent />
  }

  if (!eligible) {
    return (
      <div className="unauthorized">
        <h2>Unauthorized</h2>
        <p>You do not have access to this page.</p>
      </div>
    )
  }

  const sidebarVariants = {
    hidden: { width: 0, opacity: 0 },
    visible: {
      width: sidebarCollapsed ? 80 : 280,
      opacity: 1,
      transition: { duration: 0.3, ease: 'easeInOut' }
    },
    exit: {
      width: 0,
      opacity: 0,
      transition: { duration: 0.3, ease: 'easeInOut' }
    }
  }

  const navbarVariants = {
    hidden: { height: 0, opacity: 0 },
    visible: {
      height: 60,
      opacity: 1,
      transition: { duration: 0.3, ease: 'easeInOut' }
    },
    exit: {
      height: 0,
      opacity: 0,
      transition: { duration: 0.3, ease: 'easeInOut' }
    }
  }

  const contentVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: 'easeInOut' }
    }
  }

  return (
    <div className={`dashboard-container${sidebarCollapsed ? ' sidebar-collapsed' : ''}${isKbNavbarMode ? ' kb-navbar-mode' : ''}`}>
      <AnimatePresence mode="wait">
        {isKbNavbarMode ? (
          <motion.nav
            key="navbar"
            className="kb-navbar"
            variants={navbarVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="navbar-brand">Dashboard</div>
            <div className="navbar-logo">
              <img src="https://cdn.ballhead.app/web_assets/logo.png" alt="Logo" />
            </div>
            <ul className="navbar-nav">
              <li
                className={selectedSection === 'Profile' ? 'active' : ''}
                onClick={() => {
                  dispatch(setSelectedSection('Profile'))
                  setIsKbNavbarMode(false)
                }}
              >
                <span className="icon">👤</span>
                <span>Profile</span>
              </li>
              {permissions?.knowledge_base && (
                <li
                  className={selectedSection === 'Knowledge Base' ? 'active' : ''}
                  onClick={() => {
                    dispatch(setSelectedSection('Knowledge Base'))
                    setIsKbNavbarMode(true)
                  }}
                >
                  <span className="icon">📚</span>
                  <span>Knowledge Base</span>
                </li>
              )}
              {permissions?.gc_items && (
                <li
                  className={selectedSection === 'Item Category' ? 'active' : ''}
                  onClick={() => {
                    dispatch(setSelectedSection('Item Category'))
                    setIsKbNavbarMode(false)
                  }}
                >
                  <span className="icon">🛍️</span>
                  <span>Item Category</span>
                </li>
              )}
            </ul>
          </motion.nav>
        ) : (
          <motion.aside
            key="sidebar"
            className={`sidebar${sidebarCollapsed ? ' collapsed' : ''}`}
            variants={sidebarVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <button className="collapse-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
              <img
                src={sidebarCollapsed ? EXPAND_ICON : COLLAPSE_ICON}
                alt={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                className="collapse-icon"
              />
            </button>
            <div className="sidebar-header">
              {!sidebarCollapsed && <span>Dashboard</span>}
            </div>
            <ul className="sidebar-nav">
              <li
                className={selectedSection === 'Profile' ? 'active' : ''}
                onClick={() => {
                  dispatch(setSelectedSection('Profile'))
                  setIsKbNavbarMode(false)
                }}
              >
                <span className="icon">👤</span>
                <span className="label">Profile</span>
                <span className="tooltip">Profile</span>
              </li>
              {permissions?.knowledge_base && (
                <li
                  className={selectedSection === 'Knowledge Base' ? 'active' : ''}
                  onClick={() => {
                    dispatch(setSelectedSection('Knowledge Base'))
                    setIsKbNavbarMode(true)
                  }}
                >
                  <span className="icon">📚</span>
                  <span className="label">Knowledge Base</span>
                  <span className="tooltip">Knowledge Base</span>
                </li>
              )}
              {permissions?.gc_items && (
                <li
                  className={selectedSection === 'Item Category' ? 'active' : ''}
                  onClick={() => {
                    dispatch(setSelectedSection('Item Category'))
                    setIsKbNavbarMode(false)
                  }}
                >
                  <span className="icon">🛍️</span>
                  <span className="label">Item Category</span>
                  <span className="tooltip">Item Category</span>
                </li>
              )}
            </ul>
          </motion.aside>
        )}
      </AnimatePresence>

      <motion.main
        className="main-content"
        variants={contentVariants}
        initial="hidden"
        animate="visible"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={`${selectedSection}-${isKbNavbarMode}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </motion.main>
    </div>
  )
}